import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { setupDocumoFax } from 'api/Documo';
import { setupTwilioNumber } from 'api/Twilio';

import useCopy from 'lib/hooks/useCopy';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Divider from 'components/shared/Divider/Divider';

import FaxSection from './components/FaxSection';
import ContactFields from './components/ContactFields';
import { getInitialValues } from './lib/getInitialValues';
import usePracticeContactInfoFormik from './lib/contactInfoFormik';
import { useContactInfoEffects } from './lib/useContactInfoEffects';
import PatientCommunicationSection from './components/PatientCommunicationSection';

const ContactInfoForm = ({
  addFooterActions,
  setHeaderButtonProps,
  onNext,
  mutateOrchestratorData,
  orchestratorLoading,
  navigateToSubstep
}) => {
  const { practice } = useOutletContext();
  const navigate = useNavigate();
  const { copyToClipboard } = useCopy();
  const [twilioError, setTwilioError] = useState(null);

  const initialValues = getInitialValues(practice);

  const formik = usePracticeContactInfoFormik(initialValues, async (values) => {
    await mutateOrchestratorData(values);
  });

  const { mutate: setupFax, isPending } = useCustomMutation({
    mutationFn: () => setupDocumoFax(navigate, { practice_id: practice.id }),
    invalidateQueryKey: 'practice'
  });

  const { mutate: mutateTwilioNumber } = useCustomMutation({
    mutationFn: () => setupTwilioNumber(navigate, { practiceId: practice.id }),
    invalidateQueryKey: 'practice',
    disableAlert: true,
    handleResponse: (data) => {
      if (data?.code !== 0) {
        setTwilioError("Couldn't generate a patient communication number. Please contact support.");
      } else {
        setTwilioError(null);
      }
    }
  });

  useContactInfoEffects({
    formik,
    practice,
    setHeaderButtonProps,
    addFooterActions,
    orchestratorLoading,
    onNext,
    mutateTwilioNumber,
    setTwilioError
  });

  return (
    <>
      <ContactFields formik={formik} />
      <Divider className="my-6" />
      <PatientCommunicationSection
        formik={formik}
        practiceAddress={practice?.practiceAddress}
        navigateToSubstep={navigateToSubstep}
        copyToClipboard={copyToClipboard}
        twilioError={twilioError}
      />
      <Divider className="my-6" />
      <FaxSection
        formik={formik}
        copyToClipboard={copyToClipboard}
        setupFax={setupFax}
        isPending={isPending}
      />
    </>
  );
};

export default ContactInfoForm;
